import { useEffect, useState } from "react";
import { api, wsBaseUrl } from "@/lib/api";

const POLL_MS = 5000;
const RETRY_MS = 10000;
const EMPTY = { rows: [], newest: [], desks: null, recent: [], p2pStats: null, usdtInr: null, live: false, updatedAt: null };

/** One shared socket per quote, ref-counted across components on the page. */
const stores = {};

function emit(store, patch) {
  store.state = { ...store.state, ...patch };
  store.subs.forEach((fn) => fn(store.state));
}

function applySnapshot(store, msg) {
  emit(store, {
    rows: msg.rows || [],
    newest: msg.new || store.state.newest,
    desks: msg.desks || store.state.desks,
    recent: msg.recent || store.state.recent,
    p2pStats: msg.p2p_stats || store.state.p2pStats,
    usdtInr: msg.usdt_inr ?? store.state.usdtInr,
    live: true,
    updatedAt: Date.now(),
  });
}

async function pollOnce(store, quote) {
  try {
    const [list, fresh, status, recent, fx] = await Promise.all([
      api.get("/api/markets/list", { params: { quote, category: "all", page_size: 500 } }),
      store.state.newest.length ? null : api.get("/api/markets/list", { params: { quote, category: "new", page_size: 30 } }).catch(() => null),
      api.get("/api/markets/status").catch(() => null),
      api.get("/api/markets/recent-trades").catch(() => null),
      api.get("/api/markets/fx/usdt-inr").catch(() => null),
    ]);
    applySnapshot(store, {
      rows: list.data?.rows,
      new: fresh ? (fresh.data?.rows || []).map((r) => r.symbol) : undefined,
      desks: status?.data,
      recent: recent?.data?.trades,
      p2p_stats: recent?.data?.p2p_stats,
      usdt_inr: fx?.data?.rate,
    });
  } catch {
    emit(store, { live: false });
  }
}

function startPolling(store, quote) {
  if (store.pollTimer) return;
  pollOnce(store, quote);
  store.pollTimer = setInterval(() => pollOnce(store, quote), POLL_MS);
}

function stopPolling(store) {
  clearInterval(store.pollTimer);
  store.pollTimer = null;
}

function connect(store, quote) {
  const root = wsBaseUrl();
  if (!root || typeof WebSocket === "undefined") {
    startPolling(store, quote);
    return;
  }
  let ws;
  try {
    ws = new WebSocket(`${root}/api/ws/markets?quote=${encodeURIComponent(quote)}`);
  } catch {
    startPolling(store, quote);
    return;
  }
  store.ws = ws;
  ws.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      if (msg.type === "markets" && msg.quote === quote) {
        stopPolling(store);
        applySnapshot(store, msg);
      }
    } catch {
      /* ignore malformed frames */
    }
  };
  ws.onclose = () => {
    if (store.ws !== ws) return;
    store.ws = null;
    if (!store.subs.size) return;
    startPolling(store, quote);
    store.retryTimer = setTimeout(() => connect(store, quote), RETRY_MS);
  };
}

function subscribe(quote, fn) {
  let store = stores[quote];
  if (!store) {
    store = stores[quote] = { state: EMPTY, subs: new Set(), ws: null, pollTimer: null, retryTimer: null };
  }
  store.subs.add(fn);
  clearTimeout(store.releaseTimer);
  fn(store.state);
  if (store.subs.size === 1) {
    if (!store.state.rows.length) pollOnce(store, quote);
    const ws = store.ws;
    if (!ws || ws.readyState === WebSocket.CLOSED) connect(store, quote);
  }
  return () => {
    store.subs.delete(fn);
    if (store.subs.size) return;
    // React remounts effects immediately. Closing a socket that is still
    // connecting logs "WebSocket is closed before the connection is established".
    clearTimeout(store.releaseTimer);
    store.releaseTimer = setTimeout(() => {
      if (store.subs.size) return;
      clearTimeout(store.retryTimer);
      stopPolling(store);
      const ws = store.ws;
      store.ws = null;
      if (ws && ws.readyState !== WebSocket.CLOSED) ws.close();
    }, 250);
  };
}

/**
 * Live website markets (admin listing/featured rules applied server-side).
 * Returns { rows, newest, desks, recent, usdtInr, live, updatedAt }.
 */
export function useLiveMarkets(quote = "USDT") {
  const [state, setState] = useState(() => stores[quote]?.state || EMPTY);
  useEffect(() => subscribe(quote, setState), [quote]);
  return state;
}

/** P2P / OTC availability per coin, as set on the admin Markets page. */
export function useDeskStatus(quote = "USDT") {
  return useLiveMarkets(quote).desks;
}
